// Global status catalog (DEC-0452). One catalog for every task type.
//
// Four meaning families are locked: פתוח / בביצוע / בהקפאה / סגור. Every status
// belongs to EXACTLY ONE family, and logic (filters, "is this task done?",
// overdue rules) reads the family — never the status name. A status can be
// renamed or added without touching any rule.
//
// Each task type points at a status group (an ordered subset of the catalog).
// A group is picked from existing ones or created fresh.
//
// Stored value on an instance stays a plain string key in meta.status, so the
// four legacy keys (todo / in-progress / done / cancelled) are kept as keys of
// their seed statuses and existing data needs no migration.
//
// Pure module: no I/O. Persistence is a separate concern (see the prepared
// migration for the task_status / task_status_group entity types).

export const STATUS_FAMILIES = ['open', 'active', 'frozen', 'closed'] as const;
export type StatusFamily = (typeof STATUS_FAMILIES)[number];

export const STATUS_FAMILY_LABEL: Record<StatusFamily, string> = {
  open: 'פתוח',
  active: 'בביצוע',
  frozen: 'בהקפאה',
  closed: 'סגור',
};

export interface StatusDef {
  key: string;
  label: string;
  family: StatusFamily;
  sortOrder: number;
  /** Seed statuses ship with the code; custom ones are created by people. */
  builtin: boolean;
  /** Closed family only: the task ended WITHOUT its work being completed
   *  (cancelled, not relevant, duplicate). Unset means completed. Dependents
   *  of a dropped task are released with a warning, not silently. */
  dropped?: boolean;
}

export interface StatusGroup {
  key: string;
  label: string;
  /** Ordered status keys. Every key must exist in the catalog. */
  statusKeys: string[];
  builtin: boolean;
}

export interface StatusCatalog {
  statuses: StatusDef[];
  groups: StatusGroup[];
}

function seed(key: string, label: string, family: StatusFamily, sortOrder: number): StatusDef {
  return { key, label, family, sortOrder, builtin: true };
}

// The candidate list agreed with Gal on 20.09. The four legacy keys keep their
// stored spelling; everything else is new.
export const SEED_STATUSES: StatusDef[] = [
  seed('todo', 'חדש', 'open', 10),
  seed('queued', 'בתור', 'open', 20),
  seed('ready', 'מוכן להתחלה', 'open', 30),
  seed('needs-details', 'ממתין לפרטים', 'open', 40),
  seed('in-progress', 'בעבודה', 'active', 50),
  seed('in-review', 'בבדיקה', 'active', 60),
  seed('awaiting-approval', 'ממתין לאישור', 'active', 70),
  seed('frozen', 'מוקפא', 'frozen', 80),
  seed('waiting-external', 'ממתין לגורם חיצוני', 'frozen', 90),
  seed('deferred', 'נדחה לתאריך', 'frozen', 100),
  seed('done', 'הושלם', 'closed', 110),
  { ...seed('cancelled', 'בוטל', 'closed', 120), dropped: true },
  { ...seed('not-relevant', 'לא רלוונטי', 'closed', 130), dropped: true },
  { ...seed('duplicate', 'כפול', 'closed', 140), dropped: true },
];

export const DEFAULT_STATUS_GROUP_KEY = 'general';

export const SEED_GROUPS: StatusGroup[] = [
  {
    key: DEFAULT_STATUS_GROUP_KEY,
    label: 'כללי',
    statusKeys: SEED_STATUSES.map((s) => s.key),
    builtin: true,
  },
  {
    key: 'simple',
    label: 'פשוט',
    statusKeys: ['todo', 'in-progress', 'done', 'cancelled'],
    builtin: true,
  },
];

export const SEED_CATALOG: StatusCatalog = {
  statuses: SEED_STATUSES,
  groups: SEED_GROUPS,
};

export function isStatusFamily(v: unknown): v is StatusFamily {
  return typeof v === 'string' && (STATUS_FAMILIES as readonly string[]).includes(v);
}

export function findStatus(catalog: StatusCatalog, key: string | null | undefined): StatusDef | undefined {
  if (!key) return undefined;
  return catalog.statuses.find((s) => s.key === key);
}

/** The family of a stored status key, or null when the key is unknown. Logic
 *  must go through this rather than compare names. */
export function statusFamily(catalog: StatusCatalog, key: string | null | undefined): StatusFamily | null {
  return findStatus(catalog, key)?.family ?? null;
}

/** True when a stored meta.status can be edited through the catalog: empty, or
 *  a known key. Other entity types keep their own status vocabularies in the
 *  same meta.status slot (measured 20.09: business_function "active", tool
 *  "experimental"/"external"/"preferred"). Those must not be relabelled
 *  "not in catalog" or overwritten by the picker, so the host falls back to the
 *  plain select for them. */
export function isCatalogStatusValue(catalog: StatusCatalog, key: unknown): boolean {
  if (key == null || key === '') return true;
  return typeof key === 'string' && !!findStatus(catalog, key);
}

export function isClosedStatus(catalog: StatusCatalog, key: string | null | undefined): boolean {
  return statusFamily(catalog, key) === 'closed';
}

/** True for a closed status that ended without the work being completed. */
export function isDroppedStatus(catalog: StatusCatalog, key: string | null | undefined): boolean {
  const s = findStatus(catalog, key);
  return s?.family === 'closed' && s.dropped === true;
}

export function statusesByFamily(statuses: StatusDef[]): Record<StatusFamily, StatusDef[]> {
  const out: Record<StatusFamily, StatusDef[]> = { open: [], active: [], frozen: [], closed: [] };
  for (const s of [...statuses].sort((a, b) => a.sortOrder - b.sortOrder)) out[s.family].push(s);
  return out;
}

export function findGroup(catalog: StatusCatalog, key: string | null | undefined): StatusGroup | undefined {
  if (!key) return undefined;
  return catalog.groups.find((g) => g.key === key);
}

/** The statuses a task of this group may use, in group order. Unknown keys in
 *  the group are skipped rather than thrown, so a deleted status cannot break
 *  the picker. An unknown group falls back to the default group. */
export function statusesForGroup(catalog: StatusCatalog, groupKey: string | null | undefined): StatusDef[] {
  const group = findGroup(catalog, groupKey) ?? findGroup(catalog, DEFAULT_STATUS_GROUP_KEY);
  if (!group) return [];
  return group.statusKeys
    .map((k) => findStatus(catalog, k))
    .filter((s): s is StatusDef => !!s);
}

export type CatalogResult =
  | { ok: true; catalog: StatusCatalog; created: StatusDef }
  | { ok: false; error: string };

function slugify(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'status';
}

/** Adds a custom status. The family is REQUIRED and single-valued (Gal, 20.09:
 *  "משפחה אחת בלבד, התיבה היא בחירה בודדת"). A label already in use — in any
 *  family — is refused, so two statuses can never read the same. */
export function addStatus(
  catalog: StatusCatalog,
  input: {
    label: string;
    family: StatusFamily | null | undefined;
    groupKeys?: string[];
    /** Closed family only: ended without the work being completed. */
    dropped?: boolean;
  },
): CatalogResult {
  const label = input.label.trim();
  if (!label) return { ok: false, error: 'שם הסטטוס ריק' };
  if (!isStatusFamily(input.family)) return { ok: false, error: 'חובה לבחור משפחה אחת' };
  if (input.dropped && input.family !== 'closed') {
    return { ok: false, error: 'רק סטטוס במשפחת "סגור" יכול להיות מסומן כבוטל' };
  }
  if (catalog.statuses.some((s) => s.label === label)) {
    return { ok: false, error: `"${label}" כבר קיים בקטלוג` };
  }

  const base = slugify(label);
  let key = base;
  for (let n = 2; catalog.statuses.some((s) => s.key === key); n += 1) key = `${base}-${n}`;

  const nextOrder = catalog.statuses.reduce((m, s) => Math.max(m, s.sortOrder), 0) + 10;
  const created: StatusDef = {
    key, label, family: input.family, sortOrder: nextOrder, builtin: false,
    ...(input.dropped ? { dropped: true } : {}),
  };
  const targets = new Set(input.groupKeys ?? []);

  return {
    ok: true,
    created,
    catalog: {
      statuses: [...catalog.statuses, created],
      groups: catalog.groups.map((g) =>
        targets.has(g.key) ? { ...g, statusKeys: [...g.statusKeys, key] } : g,
      ),
    },
  };
}

/** Creates a new status group from existing statuses (a "new status group" per
 *  task type). Refuses an empty group and unknown status keys. */
export function addGroup(
  catalog: StatusCatalog,
  input: { label: string; statusKeys: string[] },
): { ok: true; catalog: StatusCatalog; created: StatusGroup } | { ok: false; error: string } {
  const label = input.label.trim();
  if (!label) return { ok: false, error: 'שם הקבוצה ריק' };
  if (input.statusKeys.length === 0) return { ok: false, error: 'קבוצה חייבת לכלול סטטוס אחד לפחות' };
  const known = new Set(catalog.statuses.map((s) => s.key));
  const unknown = input.statusKeys.find((k) => !known.has(k));
  if (unknown) return { ok: false, error: `סטטוס לא קיים: ${unknown}` };
  if (catalog.groups.some((g) => g.label === label)) {
    return { ok: false, error: `"${label}" כבר קיימת` };
  }

  const base = slugify(label);
  let key = base;
  for (let n = 2; catalog.groups.some((g) => g.key === key); n += 1) key = `${base}-${n}`;
  const created: StatusGroup = { key, label, statusKeys: [...new Set(input.statusKeys)], builtin: false };
  return { ok: true, created, catalog: { ...catalog, groups: [...catalog.groups, created] } };
}
